import React from "react";
import { Formik } from "formik";
import { useParams } from "react-router";
import {
  getCustomerDatacenters,
  getPowerReportConfigurations,
  updatePowerReportConfigurations,
} from "../../../services/customers/CustomerService";
import { Loading } from "../../../services/Loading";
import { IDataCenter, IPowerReportConfigurations } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import * as yup from "yup";
import {
  Dropdown,
  IBasePickerSuggestionsProps,
  IDropdownOption,
  ITag,
  Stack,
  TagPicker,
  Toggle,
  TooltipHost,
} from "office-ui-fabric-react";
import NoDataTile from "../../common/NoDataTile";
import { parseJwt } from "../../../services/UtilService";
import { mdiCheck, mdiClose, mdiPencil } from "@mdi/js";
import Icon from "@mdi/react";
import { THEME } from "../../../utils/Styles";

export interface ReportConfigurationProps {
  isPdu: boolean;
}

// const reportConfigSchema = yup.object({
//   customerId: yup.string().required("required"),
//   datacenterId: yup.string().required("required"),
//   dedicated: yup.array().required("select at least one"),
//   rackwise: yup.array().required("select at least one"),
// });

const dedicatedTag: ITag[] = [
  { key: "kwh", name: "KWH" },
  { key: "kva", name: "KVA" },
  { key: "kw", name: "KW" },
];

const rackwiseTag: ITag[] = [
  { key: "kw", name: "KW" },
  { key: "kwh", name: "KWH" },
  { key: "amps", name: "AMPS" },
  { key: "kva", name: "KVA" },
];

const dedicatedDict: { [key: string]: string } = {
  kwh: "KWH",
  kva: "KVA",
  kw: "KW",
};

const rackwiseDict: { [key: string]: string } = {
  kw: "KW",
  kwh: "KWH",
  amps: "AMPS",
  kva: "KVA",
};

const groupByOptions_Pdu: IDropdownOption[] = [
  { key: "floor-wise", text: "Floor Wise" },
  { key: "quadrant-wise", text: "Quadrant Wise" },
];

const groupByOptions_TempRh: IDropdownOption[] = [
  { key: "floor-wise", text: "Floor Wise" },
  { key: "quadrant-wise", text: "Quadrant Wise" },
  { key: "dc-wise", text: "Datacenter Wise" },
];

const groupByDict: { [key: string]: string } = {
  "floor-wise": "Floor Wise",
  "quadrant-wise": "Quadrant Wise",
  "dc-wise": "Datacenter Wise",
};

const billing_Unit: IDropdownOption[] = [
  { key: "rackwise-kw", text: "Rackwise KW" },
  { key: "rackwise-kwh", text: "Rackwise KWH" },
  { key: "rackwise-kva", text: "Rackwise KVA" },
  { key: "dedicated-kw", text: "Dedicated KW" },
  { key: "dedicated-kwh", text: "Dedicated KWH" },
  { key: "dedicated-kva", text: "Dedicated KVA" },
];

const billing_Unit_Dict: { [key: string]: string } = {
  "rackwise-kw": "Rackwise KW",
  "rackwise-kwh": "Rackwise KWH",
  "rackwise-kva": "Rackwise KVA",
  "dedicated-kw": "Dedicated KW",
  "dedicated-kwh": "Dedicated KWH",
  "dedicated-kva": "Dedicated KVA",
};

const billing_Unit_MC: IDropdownOption[] = [
  { key: "kw", text: "KW" },
  { key: "kwh", text: "KWH" },
  { key: "kva", text: "KVA" },
];

const ReportConfiguration: React.FC<ReportConfigurationProps> = ({ isPdu }) => {
  //custom hooks
  const params: any = useParams();
  const { showToast } = React.useContext(ToastContext);

  const tokenData = parseJwt();

  //state
  const [state, setState] = React.useState({
    isLoading: false,
    reportConfig: [],
    datacenters: [],
    isOnEdit: false,
    index: undefined,
    reportConfigObj: {
      customerId: params.customerId,
      datacenterId: "",
      dedicated: [],
      rackwise: [],
      groupByPdu: "floor-wise",
      groupByTempRh: "quadrant-wise",
      hideRackWiseReport: false,
      hideDedicatedReport: false,
      isDedicatedPduShowInRackwiseTable: true,
      billingUnit: "rackwise-kw",
      billingUnitForMinimumCommitted: "kw",
    },
    defDedicatedUnit: [],
    defRackwiseUnit: [],
  } as {
    isLoading: boolean;
    reportConfig: IPowerReportConfigurations[];
    datacenters: any;
    isOnEdit: boolean;
    index?: number;
    reportConfigObj: IPowerReportConfigurations;
    defDedicatedUnit: ITag[];
    defRackwiseUnit: ITag[];
  });

  React.useEffect(() => {
    const asyncFunc = async () => {
      await getAllDatacenter();
    };
    asyncFunc();
  }, []);

  const getAllDatacenter = async () => {
    setState({ ...state, isLoading: true });
    let result = await getCustomerDatacenters({
      customerId: params.customerId,
      isLite: false,
    });
    if (result?.success) {
      getPowerReportConfig(result.data);
    } else {
      setState({ ...state, isLoading: false });
      showToast(
        result.message || "Error while getting customer datacenters",
        "error"
      );
    }
  };

  const getPowerReportConfig = async (datacenters: any) => {
    setState({ ...state, isLoading: true });
    let result = await getPowerReportConfigurations({
      customerId: params.customerId,
    });
    if (result?.success) {
      let reportConfig: IPowerReportConfigurations[] = [];

      datacenters.forEach((datacenter: IDataCenter) => {
        let item = result.data.find((i) => i.datacenterId === datacenter._id);
        if (item) {
          item["datacenterName"] = datacenter.name;
          reportConfig.push(item);
        } else {
          reportConfig.push({
            datacenterId: datacenter._id as string,
            datacenterName: datacenter.name,
            customerId: params.customerId,
            dedicated: ["kwh"],
            rackwise: ["amps", "kw"],
            groupByPdu: "floor-wise",
            groupByTempRh: "quadrant-wise",
            hideDedicatedReport: false,
            hideRackWiseReport: false,
            isDedicatedPduShowInRackwiseTable: true,
            billingUnit: "rackwise-kw",
            billingUnitForMinimumCommitted: "kw",
          });
        }
      });

      setState({
        ...state,
        datacenters,
        reportConfig,
        isLoading: false,
      });
    } else {
      setState({ ...state, isLoading: false });
      showToast(
        result.message || "Error while getting minimum committed power",
        "error"
      );
    }
  };

  //   const handleLoading = () => {
  //     setState({
  //       ...state,
  //       isLoading: !state.isLoading,
  //     });
  //   };

  const onUpdateReportConfig = async () => {
    // if (
    //   state.reportConfigObj.dedicated.length < 1 ||
    //   state.reportConfigObj.rackwise.length < 1
    // ) {
    //   showToast("select at least one", "error");
    // } else {
    setState({ ...state, isLoading: true });
    let reportConfigObj = { ...state.reportConfigObj };
    reportConfigObj.rackwise.sort();
    let result = await updatePowerReportConfigurations(state.reportConfigObj);
    if (result?.success) {
      let reportConfig = [...state.reportConfig];
      if (state.index !== undefined) {
        reportConfig[state.index].dedicated = result.data?.dedicated || [];
        reportConfig[state.index].rackwise = result.data?.rackwise || [];
        reportConfig[state.index].groupByPdu =
          result.data?.groupByPdu || "floor-wise";
        reportConfig[state.index].groupByTempRh =
          result.data?.groupByTempRh || "quadrant-wise";
        reportConfig[state.index].billingUnit =
          result.data?.billingUnit || "rackwise-kw";
        reportConfig[state.index].billingUnitForMinimumCommitted =
          result.data?.billingUnitForMinimumCommitted || "kw";
      }
      setState({
        ...state,
        reportConfig,
        isOnEdit: false,
        index: undefined,
        reportConfigObj: {
          ...state.reportConfigObj,
          datacenterId: "",
          dedicated: [],
          rackwise: [],
          groupByTempRh: "quadrant-wise",
          groupByPdu: "floor-wise",
          billingUnit: "rackwise-kw",
          billingUnitForMinimumCommitted: "kw",
        },
        isLoading: false,
      });
      showToast("Report configurations updated successfully", "success");
    } else {
      setState({ ...state, isLoading: false });
      showToast(
        result.message || "Error while updating report configurations",
        "error"
      );
    }
    // }
  };

  const onEdit = (
    index: number,
    datacenterId: string,
    dedicated: string[],
    rackwise: string[],
    groupByPdu: string,
    groupByTempRh: string,
    hideDedicatedReport: boolean,
    hideRackWiseReport: boolean,
    billingUnit: string,
    isDedicatedPduShowInRackwiseTable: boolean,
    billingUnitForMinimumCommitted: string
  ) => {
    let defDedicatedUnit: any = dedicated.map((d) => {
      return dedicatedTag.find((ele) => ele.key === d);
    });
    let defRackwiseUnit: any = rackwise.map((r) => {
      return rackwiseTag.find((ele) => ele.key === r);
    });
    setState({
      ...state,
      isOnEdit: true,
      index,
      reportConfigObj: {
        ...state.reportConfigObj,
        datacenterId: datacenterId || "",
        dedicated: dedicated || [],
        rackwise: rackwise || [],
        groupByPdu,
        groupByTempRh,
        hideDedicatedReport,
        hideRackWiseReport,
        billingUnit,
        isDedicatedPduShowInRackwiseTable,
        billingUnitForMinimumCommitted,
      },
      defDedicatedUnit,
      defRackwiseUnit,
    });
  };

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  const filterSuggestedTagsForDedicated = (
    filterText: string,
    tagList: ITag[]
  ): ITag[] => {
    return filterText
      ? dedicatedTag.filter(
        (tag) =>
          tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
          !listContainsTagList(tag, tagList)
      )
      : [];
  };

  const filterSuggestedTagsForRackwise = (
    filterText: string,
    tagList: ITag[]
  ): ITag[] => {
    return filterText
      ? rackwiseTag.filter(
        (tag) =>
          tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
          !listContainsTagList(tag, tagList)
      )
      : [];
  };

  const pickerSuggestionsPropsForDedicated: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No dedicated unit found",
  };

  const pickerSuggestionsPropsForRackwise: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No rackwise unit found",
  };

  const showAllTagsForDedicated = (selectedItems?: ITag[] | undefined) => {
    return removeDuplicates(dedicatedTag, selectedItems);
  };

  const showAllTagsForRackwise = (selectedItems?: ITag[] | undefined) => {
    return removeDuplicates(rackwiseTag, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <div>
        {state.datacenters && state.datacenters.length > 0 ? (
          <div className="report-config-table">
            <Formik
              enableReinitialize
              initialValues={state.reportConfigObj}
              //   validationSchema={reportConfigSchema}
              onSubmit={async (
                values: IPowerReportConfigurations,
                { setSubmitting }
              ) => {
                // let data = { ...values };
                // handleLoading();
                // let result = await updatePowerReportConfigurations(data);
                // handleLoading();
                // if (result?.success) {
                //   let reportConfig = [...state.reportConfig];
                //   if (state.index !== undefined) {
                //     reportConfig[state.index].dedicated =
                //       result.data?.dedicated || [];
                //     reportConfig[state.index].rackwise =
                //       result.data?.rackwise || [];
                //   }
                //   setState({
                //     ...state,
                //     reportConfig,
                //     isOnEdit: false,
                //     index: undefined,
                //     reportConfigObj: {
                //       ...state.reportConfigObj,
                //       datacenterId: "",
                //       dedicated: [],
                //       rackwise: [],
                //     },
                //   });
                //   showToast(
                //     "Report configurations updated successfully",
                //     "success"
                //   );
                // } else {
                //   //   setState({ ...state, isOnEdit: false });
                //   showToast(
                //     result.message ||
                //       "Error while updating report configurations",
                //     "error"
                //   );
                // }
                setSubmitting(false);
              }}
            >
              {({ handleSubmit, values }) => (
                <form onSubmit={handleSubmit}>
                  <Stack className="pt-16">
                    <Stack.Item>
                      <div className="office-table bg-white">
                        <table className="ms-Table bordered-table w-100">
                          <thead className="html-table-thead">
                            <tr style={{ height: 44 }}>
                              <th>Datacenter Name</th>
                              {isPdu === true && <th>Dedicated</th>}
                              {isPdu === true && <th>Rackwise</th>}
                              <th>Group By</th>
                              {isPdu === true && <th>Billing Unit</th>}
                              {isPdu === true && (
                                <th>Minimum Committed Billing Unit</th>
                              )}
                              {isPdu === true && (
                                <th>Show Dedicated Pdu in Rackwise Table</th>
                              )}
                              {![2, 4, 5].includes(tokenData.role) && <th>Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {state.reportConfig.map(
                              (
                                ele: IPowerReportConfigurations,
                                index: number
                              ) => {
                                return (
                                  <tr key={index}>
                                    {/* <td>
                                      {state.datacenters.find(
                                        (d: IDataCenter) =>
                                          d._id === ele.datacenterId
                                      )?.name || ""}
                                    </td> */}
                                    <td align="center">{ele.datacenterName}</td>
                                    {state.isOnEdit && state.index === index
                                      ? isPdu === true && (
                                        <td width={250}>
                                          <TagPicker
                                            defaultSelectedItems={
                                              state.defDedicatedUnit
                                            }
                                            removeButtonAriaLabel="Remove"
                                            onResolveSuggestions={
                                              filterSuggestedTagsForDedicated as any
                                            }
                                            pickerSuggestionsProps={
                                              pickerSuggestionsPropsForDedicated
                                            }
                                            onChange={(items: any) => {
                                              let dedicated: any = [];
                                              items.forEach(
                                                (element: any) => {
                                                  dedicated.push(element.key);
                                                }
                                              );
                                              setState({
                                                ...state,
                                                reportConfigObj: {
                                                  ...state.reportConfigObj,
                                                  dedicated,
                                                },
                                              });
                                            }}
                                            onEmptyResolveSuggestions={
                                              showAllTagsForDedicated
                                            }
                                          />
                                        </td>
                                      )
                                      : isPdu === true && (
                                        <td
                                          align={
                                            ele.dedicated.length < 1
                                              ? "center"
                                              : "left"
                                          }
                                        >
                                          {ele.dedicated.length > 0
                                            ? ele.dedicated
                                              .map((d) => dedicatedDict[d])
                                              .join(", ")
                                            : "-"}
                                        </td>
                                      )}
                                    {state.isOnEdit && state.index === index
                                      ? isPdu === true && (
                                        <td width={250}>
                                          <TagPicker
                                            defaultSelectedItems={
                                              state.defRackwiseUnit
                                            }
                                            removeButtonAriaLabel="Remove"
                                            onResolveSuggestions={
                                              filterSuggestedTagsForRackwise as any
                                            }
                                            pickerSuggestionsProps={
                                              pickerSuggestionsPropsForRackwise
                                            }
                                            onChange={(items: any) => {
                                              let rackwise: any = [];
                                              items.forEach(
                                                (element: any) => {
                                                  rackwise.push(element.key);
                                                }
                                              );
                                              setState({
                                                ...state,
                                                reportConfigObj: {
                                                  ...state.reportConfigObj,
                                                  rackwise,
                                                },
                                              });
                                            }}
                                            onEmptyResolveSuggestions={
                                              showAllTagsForRackwise
                                            }
                                          />
                                        </td>
                                      )
                                      : isPdu === true && (
                                        <td
                                          align={
                                            ele.rackwise.length < 1
                                              ? "center"
                                              : "left"
                                          }
                                        >
                                          {ele.rackwise.length > 0
                                            ? ele.rackwise
                                              .map((r) => rackwiseDict[r])
                                              .join(", ")
                                            : "-"}
                                        </td>
                                      )}
                                    {state.isOnEdit && state.index === index ? (
                                      <td width={250}>
                                        <Dropdown
                                          placeholder="Select an option"
                                          selectedKey={
                                            isPdu === true
                                              ? state.reportConfigObj.groupByPdu
                                              : state.reportConfigObj
                                                .groupByTempRh
                                          }
                                          // styles={{ dropdown: { width: 200 } }}
                                          options={
                                            isPdu === true
                                              ? groupByOptions_Pdu
                                              : groupByOptions_TempRh
                                          }
                                          onChange={(
                                            e,
                                            v: IDropdownOption | undefined
                                          ) => {
                                            if (v) {
                                              if (isPdu === true) {
                                                setState({
                                                  ...state,
                                                  reportConfigObj: {
                                                    ...state.reportConfigObj,
                                                    groupByPdu: v.key as string,
                                                  },
                                                });
                                              } else {
                                                setState({
                                                  ...state,
                                                  reportConfigObj: {
                                                    ...state.reportConfigObj,
                                                    groupByTempRh:
                                                      v.key as string,
                                                  },
                                                });
                                              }
                                            }
                                          }}
                                        />
                                      </td>
                                    ) : (
                                      <td align="center">
                                        {
                                          groupByDict[
                                          isPdu === true
                                            ? ele.groupByPdu
                                            : ele.groupByTempRh
                                          ]
                                        }
                                      </td>
                                    )}
                                    {state.isOnEdit && state.index === index
                                      ? isPdu === true && (
                                        <td width={250}>
                                          <Dropdown
                                            placeholder="Select a billing unit"
                                            selectedKey={
                                              state.reportConfigObj
                                                .billingUnit
                                            }
                                            // styles={{ dropdown: { width: 200 } }}
                                            options={billing_Unit}
                                            onChange={(
                                              e,
                                              v: IDropdownOption | undefined
                                            ) => {
                                              if (v) {
                                                setState({
                                                  ...state,
                                                  reportConfigObj: {
                                                    ...state.reportConfigObj,
                                                    billingUnit:
                                                      v.key as string,
                                                  },
                                                });
                                              }
                                            }}
                                          />
                                        </td>
                                      )
                                      : isPdu === true && (
                                        <td align="center">
                                          {
                                            billing_Unit_Dict[
                                            ele.billingUnit as string
                                            ]
                                          }
                                        </td>
                                      )}
                                    {state.isOnEdit && state.index === index
                                      ? isPdu === true && (
                                        <td width={250}>
                                          <Dropdown
                                            placeholder="Select a billing unit for minimum committed"
                                            selectedKey={
                                              state.reportConfigObj
                                                .billingUnitForMinimumCommitted
                                            }
                                            // styles={{ dropdown: { width: 200 } }}
                                            options={billing_Unit_MC}
                                            onChange={(
                                              e,
                                              v: IDropdownOption | undefined
                                            ) => {
                                              if (v) {
                                                setState({
                                                  ...state,
                                                  reportConfigObj: {
                                                    ...state.reportConfigObj,
                                                    billingUnitForMinimumCommitted:
                                                      v.key as string,
                                                  },
                                                });
                                              }
                                            }}
                                          />
                                        </td>
                                      )
                                      : isPdu === true && (
                                        <td width={100} align="center">
                                          {
                                            dedicatedDict[
                                            ele.billingUnitForMinimumCommitted as string
                                            ]
                                          }
                                        </td>
                                      )}
                                    {isPdu === true && (
                                      <td width={150} align="center">
                                        <Toggle
                                          checked={
                                            ele.isDedicatedPduShowInRackwiseTable
                                          }
                                          disabled={[2, 4, 5].includes(tokenData.role)}
                                          onText="Yes"
                                          offText="No"
                                          onChange={async (e, v) => {
                                            let reportConfigForDc = { ...ele };
                                            reportConfigForDc.isDedicatedPduShowInRackwiseTable =
                                              v as boolean;
                                            setState({
                                              ...state,
                                              isLoading: true,
                                            });
                                            const result =
                                              await updatePowerReportConfigurations(
                                                reportConfigForDc
                                              );
                                            if (result?.success) {
                                              let reportConfig = [
                                                ...state.reportConfig,
                                              ];

                                              if (index !== -1) {
                                                reportConfig[
                                                  index
                                                ].isDedicatedPduShowInRackwiseTable =
                                                  result.data.isDedicatedPduShowInRackwiseTable;
                                              }
                                              setState({
                                                ...state,
                                                reportConfig,
                                                isOnEdit: false,
                                                isLoading: false,
                                              });
                                            } else {
                                              setState({
                                                ...state,
                                                isLoading: false,
                                              });
                                              showToast(
                                                result.message ||
                                                "Error while updating report config",
                                                "error"
                                              );
                                            }
                                          }}
                                        />
                                      </td>
                                    )}
                                    {![2, 4, 5].includes(tokenData.role) && (
                                      <td align="center">
                                        {state.isOnEdit &&
                                          state.index === index ? (
                                          <React.Fragment>
                                            <TooltipHost
                                              content="Save"
                                              calloutProps={{
                                                gapSpace: 0,
                                              }}
                                            >
                                              <span
                                                onClick={() =>
                                                  onUpdateReportConfig()
                                                }
                                              >
                                                <Icon
                                                  path={mdiCheck}
                                                  size={0.8}
                                                  className="pointer"
                                                  color="green"
                                                />
                                              </span>
                                            </TooltipHost>
                                            &nbsp;&nbsp;
                                            <TooltipHost
                                              content="Cancel"
                                              calloutProps={{
                                                gapSpace: 0,
                                              }}
                                            >
                                              <span
                                                onClick={() =>
                                                  setState({
                                                    ...state,
                                                    isOnEdit: false,
                                                    index: undefined,
                                                    reportConfigObj: {
                                                      ...state.reportConfigObj,
                                                      datacenterId: "",
                                                      dedicated: [],
                                                      rackwise: [],
                                                      groupByTempRh:
                                                        "quadrant-wise",
                                                      groupByPdu: "floor-wise",
                                                    },
                                                  })
                                                }
                                              >
                                                <Icon
                                                  path={mdiClose}
                                                  size={0.8}
                                                  className="pointer"
                                                  color="red"
                                                />
                                              </span>
                                            </TooltipHost>
                                          </React.Fragment>
                                        ) : (
                                          <React.Fragment>
                                            <TooltipHost
                                              content="Edit"
                                              calloutProps={{
                                                gapSpace: 0,
                                              }}
                                            >
                                              <span
                                                onClick={() =>
                                                  onEdit(
                                                    index,
                                                    ele?.datacenterId,
                                                    ele?.dedicated,
                                                    ele?.rackwise,
                                                    ele?.groupByPdu,
                                                    ele?.groupByTempRh,
                                                    ele?.hideDedicatedReport as boolean,
                                                    ele?.hideRackWiseReport as boolean,
                                                    ele?.billingUnit as string,
                                                    ele?.isDedicatedPduShowInRackwiseTable as boolean,
                                                    ele?.billingUnitForMinimumCommitted as string
                                                  )
                                                }
                                              >
                                                <Icon
                                                  path={mdiPencil}
                                                  size={0.7}
                                                  className="pointer"
                                                  color={
                                                    THEME.palette.themePrimary
                                                  }
                                                />
                                              </span>
                                            </TooltipHost>
                                          </React.Fragment>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                );
                              }
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Stack.Item>
                  </Stack>
                </form>
              )}
            </Formik>
          </div>
        ) : (
          <div className="pt-16">
            <NoDataTile title="No Datacenter Found for this Customer"></NoDataTile>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default ReportConfiguration;
